import React, { FC, useRef } from 'react';
import { Group, Line, Label, Tag, Text } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDragGesture } from 'src/hooks/useDragGesture';

type Point = { x: number; y: number };

interface HorizontalGuideHandleProps {
    /** Background-image pixel-space Y (unscaled). */
    y: number;
    scale: number;
    offset: Point;
    /** Screen-space width to span the guide line across, so it reaches across the whole visible
     * canvas regardless of zoom/pan — not just across the background image itself. */
    canvasWidth: number;
    label: string;
    color: string;
    cursorChange: (cursor: string) => void;
    onDragMove: (y: number) => void;
    onDragEnd: () => void;
}

// Wider than the visible stroke so the whole line is easy to grab, not just a thin 1.5px hairline
// (and, unlike a text label, spans the entire line width).
const HIT_STROKE_WIDTH = 16;

// Rendering two identically-dashed lines on top of each other — one white, one the marker's
// color, offset by exactly one dash length — creates a continuous alternating contrast stripe
// with no gaps, ensuring visibility against any light or dark garment photo background.
const DASH_SEGMENT = 6;
const CONTRAST_COLOR = '#ffffff';

/**
 * One of the four horizontal guide lines (shoulder top, collar bottom, measure line, garment
 * bottom), draggable along its whole length, constrained to vertical movement only. Mirrors the
 * mouse-event-based dragging used in `moveGrabbers.tsx` so re-renders mid-drag don't lose the gesture.
 */
const HorizontalGuideHandle: FC<HorizontalGuideHandleProps> = ({
    y,
    scale,
    offset,
    canvasWidth,
    label,
    color,
    cursorChange,
    onDragMove,
    onDragEnd,
}) => {
    // Pure drag bookkeeping -- never read in render -- refreshed every render so `handleDragMove`
    // (registered for the whole gesture by `useDragGesture`) always sees the latest `scale`.
    const dragRef = useRef({ startY: y, clientY: 0, scale });
    dragRef.current.scale = scale;

    const handleDragMove = (e: MouseEvent) => {
        const { startY, clientY, scale: currentScale } = dragRef.current;
        const deltaY = (clientY - e.clientY) / currentScale;
        onDragMove(startY - deltaY);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onMouseDown = (e: KonvaEventObject<MouseEvent>) => {
        dragRef.current.startY = y;
        dragRef.current.clientY = e.evt.clientY;
        start();
        e.cancelBubble = true;
    };

    const screenY = y * scale + offset.y;

    return (
        <Group>
            {/* Decorative contrast stripe, underneath the interactive colored line, offset by
                one dash length so the two together cover the line with no gaps at all —
                see CONTRAST_COLOR's doc comment. */}
            <Line
                points={[0, screenY, canvasWidth, screenY]}
                stroke={CONTRAST_COLOR}
                strokeWidth={1.5}
                dash={[DASH_SEGMENT, DASH_SEGMENT]}
                listening={false}
            />
            <Line
                points={[0, screenY, canvasWidth, screenY]}
                stroke={color}
                strokeWidth={1.5}
                dash={[DASH_SEGMENT, DASH_SEGMENT]}
                dashOffset={DASH_SEGMENT}
                hitStrokeWidth={HIT_STROKE_WIDTH}
                onMouseDown={onMouseDown}
                onMouseEnter={() => cursorChange('row-resize')}
                onMouseLeave={() => cursorChange('default')}
            />
            {/* A solid background block behind the label, rather than bare colored text, so
                it stays readable regardless of what's directly behind it in the photo. */}
            <Label x={8} y={screenY - 18} listening={false}>
                <Tag
                    fill={CONTRAST_COLOR}
                    cornerRadius={2}
                    shadowColor='black'
                    shadowOpacity={0.35}
                    shadowBlur={2}
                    shadowOffsetY={1}
                />
                <Text
                    text={label}
                    fill={color}
                    fontFamily='Open Sans, sans-serif'
                    fontSize={11}
                    fontStyle='bold'
                    padding={3}
                />
            </Label>
        </Group>
    );
};

export default HorizontalGuideHandle;
